import { SagaIterator } from 'redux-saga';
import { all, call, select, take, takeLatest } from 'redux-saga/effects';
import {
  getAngebotsIds,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  storeIsAvailable,
} from '@ps20-frontend/shop-integration-redux';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PS20Logger } from '@ps20-frontend/logging';
import { App } from '@ps20-frontend/frontend-models';
import type { IAppState } from '../localStore';
import {
  dashboardWarenkorb,
  recordStateSnapshotTrigger,
  skipsRollbackAfter,
} from './stateSnapshotTrigger';

export function* watchStateSnapshotForAllApps(): SagaIterator {
  yield takeLatest(
    GlobalActions.CREATE_STATE_SNAPSHOT_FOR_ALL_APPS,
    safeSaga(createStateSnapshotForAllApps, App.SHOP),
  );
}

export function* createStateSnapshotForAllApps(action: GlobalAction<GlobalAction<unknown>>): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const allRequiredAngebotsIdSnapshots = yield call(getAngebotsIds);

  const createStateSnapshotCalls = allRequiredAngebotsIdSnapshots.map(({ appId, angebotsId }: { appId: string; angebotsId: string }) =>
    call(globalPut.put, new GlobalActions().createStateSnapshot(appId as App, angebotsId)),
  );
  yield all(createStateSnapshotCalls);

  const stateSnapshotCreatedTakeEffects = allRequiredAngebotsIdSnapshots.map(() =>
    take(GlobalActions.STATE_SNAPSHOT_CREATED),
  );
  yield all(stateSnapshotCreatedTakeEffects);

  const shopState: IAppState | undefined = yield select();
  let skipsRollback: boolean;
  try {
    skipsRollback = skipsRollbackAfter(
      action.payload,
      shopState?.transientState?.featureToggles,
      dashboardWarenkorb(),
      storeIsAvailable,
    );
  } catch (error) {
    PS20Logger.error(
      App.SHOP,
      error instanceof Error ? error : new Error(String(error)),
      `State snapshot rollback verdict failed, trigger: ${action.payload?.type}`,
    );
    skipsRollback = false;
  }
  recordStateSnapshotTrigger({
    type: action.payload?.type,
    skipsRollback,
    snapshottedOffers: allRequiredAngebotsIdSnapshots,
  });

  yield call(globalPut.put, new GlobalActions().stateSnapshotForAllAppsCreated(action.payload));
}
